import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  beverageActions,
  beverageActionTypes as BeverageActionTypes,
  beverageActionTypes,
  beverageActionTypesStepMap,
  beverageInitialMessages,
  beverageOptMessages,
  beverageStepMessages,
  beverageSuccessMessages,
  beverateStep4Messages,
  cantReadBeveragesMessage,
  consentSaveFailedMessage,
  messageTypes,
  notLoggedInMessage,
  savedMessages,
  saveMessages,
  timesMap,
} from "./BeverageConstants";
import { useBeveragesV2 } from "../../hooks/useBeveragesV2";
import { camelCaseToHumanReadable } from "../../utils/camelCaseToHumanReadable";
import { useAcid } from "../../hooks/useAcid";
import { useSessionStorage } from "../../hooks/useSessionStorage";
import { useSsoRedirect } from "../../hooks/useSsoRedirect";
import { getMessagesInitialState } from "./utils/getMessagesInitialState";
import { getStepInitialState } from "./utils/getStepInitialState";
import { DEFAULT_BEVERAGES } from "../BeverageV2/constants/default-beverages";
import actionWebInvoke from "../../utils/actionWebInvoke";
import actions from "../../config.json";
import {
  codeToCategory,
  transformAiBeverageData,
} from "../../utils/transformAiBeverageData";
import { getStoredBeverageConsent } from "../../utils/beverageSession";
import { stripAcidFromSearch } from "../../utils/postLoginRedirect";

export const useBeveragesV4 = () => {
  const location = useLocation();
  const ssoRedirect = useSsoRedirect(
    true,
    `/beverage4${stripAcidFromSearch(location.search)}`,
  );
  const {
    getBeveragesAi,
    currentBeverages,
    hasToSpecifyTimeFor,
    splitToTimeAndDrink,
    setTime,
    setBeverages,
  } = useBeveragesV2();
  const { acid } = useAcid();
  /**
   * Whether the guest has already answered the onsite-personalization
   * prompt this session — previously hardcoded to false, which forced the
   * prompt to reappear every "Repeat" cycle even after being answered.
   */
  const [beverageConsentResponded, setBeverageConsentResponded] = useState(
    () => getStoredBeverageConsent().hasResponded,
  );
  // Track the user's consent choice for this session (null = not yet set, true = opted out, false = opted in)
  const [sessionOptedOut, setSessionOptedOut] = useState(() => {
    const stored = sessionStorage.getItem("beverageConsentOptedOut");
    return stored !== null ? stored === "true" : null;
  });
  const storageKey = "beveragesV4";
  const { setItem, getItem } = useSessionStorage("messages", {
    key: storageKey,
    value: [],
  });

  // Read once at mount to keep a stable reference and prevent the auto-save effect from re-triggering on every re-render due to JSON.parse() creating a new array.
  const [cachedMessages] = useState(() => getItem("messages"));
  const [messages, setMessages] = useState(
    getMessagesInitialState(acid, cachedMessages, beverageConsentResponded),
  );
  const [step, setStep] = useState(
    getStepInitialState(acid, cachedMessages, beverageConsentResponded),
  );
  const [action, setAction] = useState(null);
  /**
   * Guards the onsite-personalization answer against double-clicks and
   * gives Actions.jsx something to disable the Send button on while a
   * retry-after-failure is in flight — without it, a fast double-tap (or a
   * retry click landing before the button re-renders as disabled) could
   * fire two overlapping saves and append duplicate chat messages.
   */
  const consentAnswerLockRef = useRef(false);
  const [isAnsweringConsent, setIsAnsweringConsent] = useState(false);

  /**
   * Serializes save calls onto a single promise chain instead of a
   * fire-and-forget lock/queue: every caller — including the initial
   * best-effort save on mount/login — gets back a real Promise<boolean> for
   * its own attempt, so a caller that needs to know whether ITS save
   * actually succeeded (e.g. the consent-correction save below) can await
   * it instead of assuming success the instant it's called.
   */
  const activeSaveRef = useRef(Promise.resolve());
  const saveBeverages = useCallback(
    (overrideOptedOut) => {
      const run = async () => {
        const structuredBeverageData =
          transformAiBeverageData(currentBeverages);
        // overrideOptedOut bypasses the stale state closure so the current consent decision is saved immediately, instead of waiting for the async state update to complete.
        const optedOut =
          overrideOptedOut !== undefined
            ? overrideOptedOut
            : sessionOptedOut !== null
              ? sessionOptedOut
              : false;

        try {
          await actionWebInvoke(
            actions["marriott-passions-v3/save-beverage-preferences"],
            {},
            {
              acid,
              structuredBeverageData,
              optedOutPersonalization: optedOut,
            },
          );
          return true;
        } catch (err) {
          console.error("Error saving beverage preferences:", err);
          return false;
        }
      };

      const result = activeSaveRef.current.then(run, run);
      activeSaveRef.current = result;
      return result;
    },
    [currentBeverages, sessionOptedOut, acid],
  );

  const sendToAi = async (message) => {
    const result = await getBeveragesAi(message);
    const hasResult = result.hasSelection && !result.hasAQuestion;
    if (hasResult) {
      setStep((prevStep) => prevStep + 1);
    } else {
      setStep((prevState) => prevState - 1);
      setMessages((messages) => [
        ...messages,
        {
          isBot: true,
          message: result.question ?? cantReadBeveragesMessage,
          messageType: messageTypes.Text,
        },
      ]);
    }
  };

  const handlePersonalization = () => {
    if (!beverageConsentResponded) {
      setMessages((messages) => [
        ...messages,
        ...saveMessages,
        ...savedMessages,
        ...beverageOptMessages,
      ]);
      setStep(
        beverageActionTypesStepMap[beverageActionTypes.OnsitePersonalization],
      );
    } else {
      setMessages([
        ...messages,
        ...saveMessages,
        ...savedMessages,
        ...beverageSuccessMessages,
      ]);
      setStep(beverageActionTypesStepMap[beverageActionTypes.ThankYou]);
    }
  };

  const next = useCallback(
    async (...params) => {
      if (step === beverageActionTypesStepMap[beverageActionTypes.Repeat]) {
        setBeverages(DEFAULT_BEVERAGES);
        setMessages(beverageInitialMessages);
        setStep(beverageActionTypesStepMap[beverageActionTypes.Start]);
        return;
      }
      const newStep = step + 1;
      switch (newStep) {
        case beverageActionTypesStepMap[beverageActionTypes.TextDisabled]: {
          setMessages((messages) => [
            ...messages,
            {
              isBot: false,
              messageType: messageTypes.Text,
              message: params[0],
            },
          ]);
          sendToAi(params[0]);
          break;
        }
        case beverageActionTypesStepMap[beverageActionTypes.SaveProfile]: {
          if (params.length) {
            setTime(params[1], params[0]);
            return;
          }
          let message = "";
          if (itemsToSpecify.length > 1) {
            message = itemsToSpecify
              .map(
                ({ item, time }) =>
                  `<b>${camelCaseToHumanReadable(item)}</b>: ${time.split(", ").map((t) => timesMap[t])}`,
              )
              .join("<br/>");
          } else {
            message = itemsToSpecify[0].time
              .split(", ")
              .map((t) => timesMap[t])
              .join(", ");
          }
          setMessages((messages) => [
            ...messages,
            {
              isBot: false,
              messageType: messageTypes.Text,
              message,
            },
            ...beverateStep4Messages,
          ]);
          break;
        }
        case beverageActionTypesStepMap[beverageActionTypes.Login]: {
          if (!acid) {
            setMessages((messages) => [
              ...messages,
              ...saveMessages,
              {
                isBot: true,
                messageType: messageTypes.Text,
                message: notLoggedInMessage,
              },
            ]);
          } else {
            saveBeverages();
            handlePersonalization();
            return;
          }
          break;
        }
        case beverageActionTypesStepMap[beverageActionTypes.LoginSuccess]: {
          setItem("messages", messages);
          ssoRedirect();
          return;
        }
        case beverageActionTypesStepMap[beverageActionTypes.ThankYou]: {
          if (
            step ===
            beverageActionTypesStepMap[
              BeverageActionTypes.OnsitePersonalization
            ]
          ) {
            if (consentAnswerLockRef.current) {
              return;
            }
            consentAnswerLockRef.current = true;
            setIsAnsweringConsent(true);

            try {
              /**
               * params[0] = true means user accepted (opted IN), so optedOut = false
               * params[0] = false means user declined (opted OUT), so optedOut = true
               */
              const userOptedOut = !params[0];

              setMessages((messages) => [
                ...messages,
                {
                  isBot: false,
                  messageType: messageTypes.Text,
                  message: params[0]
                    ? "Definitely, thanks for asking."
                    : "No, thank you.",
                },
              ]);

              /**
               * Re-save after the personalization prompt so CDP records the
               * guest's actual consent choice instead of the default value
               * sent earlier. Awaited: marking consent as answered before
               * confirming this succeeds would suppress the prompt forever
               * while CDP silently keeps holding the earlier default value.
               */
              const saved = await saveBeverages(userOptedOut);
              if (!saved) {
                setMessages((messages) => [
                  ...messages,
                  {
                    isBot: true,
                    messageType: messageTypes.Text,
                    message: consentSaveFailedMessage,
                  },
                ]);
                return;
              }

              sessionStorage.setItem("beverageConsentResponded", "true");
              setBeverageConsentResponded(true);
              sessionStorage.setItem(
                "beverageConsentOptedOut",
                userOptedOut ? "true" : "false",
              );
              setSessionOptedOut(userOptedOut);
              setMessages((messages) => [
                ...messages,
                ...beverageSuccessMessages,
              ]);
            } finally {
              consentAnswerLockRef.current = false;
              setIsAnsweringConsent(false);
            }
          } else {
            setMessages((messages) => [
              ...messages,
              ...(beverageStepMessages[newStep] ?? []),
            ]);
          }
          setItem("messages", null);
          break;
        }
        default: {
          setMessages((messages) => [
            ...messages,
            ...(beverageStepMessages[newStep] ?? []),
          ]);
        }
      }
      setStep(newStep);
    },
    [step, setTime, acid],
  );

  const itemsToSpec = useMemo(() => {
    const items = hasToSpecifyTimeFor(currentBeverages);
    return [
      ...items.selectedNonAlcoholicLovKeys,
      ...items.selectedAlcoholicLovKeys,
    ];
  }, [currentBeverages]);

  const itemsToSpecify = useMemo(() => {
    const items = hasToSpecifyTimeFor(currentBeverages);
    const allItems = [];
    items.selectedNonAlcoholicLovKeys.forEach((item) => {
      allItems.push({
        time: splitToTimeAndDrink(
          currentBeverages.beverageNonAlcoholicDesc[item],
        ).time,
        item,
      });
    });

    items.selectedAlcoholicLovKeys.forEach((item) => {
      allItems.push({
        time: splitToTimeAndDrink(currentBeverages.beverageAlcoholicDesc[item])
          .time,
        item,
      });
    });

    return allItems;
  }, [currentBeverages, hasToSpecifyTimeFor]);

  useEffect(() => {
    if (acid && cachedMessages) {
      saveBeverages();
    }
  }, [acid, cachedMessages]);

  useEffect(() => {
    if (step === beverageActionTypesStepMap[beverageActionTypes.Time]) {
      let str = ``;
      const humanReadableItems = itemsToSpec.map((i) => {
        const mapped = codeToCategory[i];
        return mapped
          ? `${mapped.type} ${mapped.category === mapped.type ? "" : mapped.category}`.toLowerCase()
          : i;
      });
      humanReadableItems.forEach((item, i) => {
        str += `${item} ${i === itemsToSpec.length - 1 ? "" : i === itemsToSpec.length - 2 ? "and " : ", "}`;
      });
      setMessages((messages) => [
        ...messages,
        {
          isBot: true,
          messageType: messageTypes.Text,
          message: `Yum! That’s a great choice. What times of day do you most enjoy your ${str}`,
        },
      ]);
    }
  }, [step]);

  useEffect(() => {
    setAction({
      ...beverageActions[step],
      itemsToSpecify,
    });
  }, [step, currentBeverages]);

  return {
    messages,
    next,
    action,
    isAnsweringConsent,
  };
};
